import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Upload, Image, FileText, Trash2, Download, X, Plus, File,
  FileSpreadsheet, Eye, Filter
} from '../../icons/wp-icons';
import {
  getProjektFotos, uploadProjektFoto, deleteProjektFoto,
  getProjektDokumente, uploadProjektDokument, downloadProjektDokument, deleteProjektDokument
} from '../../api/projektApi';
import toast from 'react-hot-toast';

// Kategorie-Optionen für Dokumente
const KATEGORIEN = ['Rechnung', 'Kostenvoranschlag', 'Angebot', 'Vertrag', 'Sonstiges'];

// Dateitype Icons und Farben
const getFileIcon = (dateityp) => {
  if (dateityp?.includes('pdf')) {
    return { icon: FileText, color: 'text-red-500', bg: 'bg-red-50' };
  }
  if (dateityp?.includes('word') || dateityp?.includes('document')) {
    return { icon: FileText, color: 'text-blue-500', bg: 'bg-blue-50' };
  }
  if (dateityp?.includes('excel') || dateityp?.includes('spreadsheet')) {
    return { icon: FileSpreadsheet, color: 'text-green-500', bg: 'bg-green-50' };
  }
  if (dateityp?.includes('image')) {
    return { icon: Image, color: 'text-purple-500', bg: 'bg-purple-50' };
  }
  return { icon: File, color: 'text-gray-500', bg: 'bg-gray-50' };
};

// Dateigröße formatieren
const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

// Kategorie Badge Farben
const getKategorieBadge = (kategorie) => {
  switch (kategorie) {
    case 'Rechnung':
      return 'bg-green-100 text-green-700';
    case 'Kostenvoranschlag':
      return 'bg-orange-100 text-orange-700';
    case 'Angebot':
      return 'bg-blue-100 text-blue-700';
    case 'Vertrag':
      return 'bg-purple-100 text-purple-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

export default function ProjektDateien({ projektId, readOnly = false }) {
  const [fotos, setFotos] = useState([]);
  const [dokumente, setDokumente] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFoto, setSelectedFoto] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [filterKategorie, setFilterKategorie] = useState('');

  const fotoInputRef = useRef(null);
  const dokumentInputRef = useRef(null);

  // Dokument-Preview State
  const [previewDokument, setPreviewDokument] = useState(null);
  const [previewPosition, setPreviewPosition] = useState({ top: 0, left: 0 });
  const [previewUrl, setPreviewUrl] = useState(null);
  const previewTimeout = useRef(null);

  // Mobile Detection
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768 || 'ontouchstart' in window);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // API URL für Vorschau
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

  useEffect(() => {
    loadData();
  }, [projektId]);

  const loadData = async () => {
    try {
      const [fotosData, dokumenteData] = await Promise.all([
        getProjektFotos(projektId).catch(() => []),
        getProjektDokumente(projektId).catch(() => [])
      ]);
      setFotos(fotosData || []);
      setDokumente(dokumenteData || []);
    } catch (error) {
      console.error('Fehler beim Laden:', error);
    } finally {
      setLoading(false);
    }
  };

  // Drag & Drop Handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith('image/')) {
        await handleFotoUploadFile(file);
      }
    }
  }, [projektId]);

  const handleFotoUploadFile = async (file) => {
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('foto', file);
      await uploadProjektFoto(projektId, formData);
      toast.success('Foto hochgeladen');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler beim Hochladen');
    } finally {
      setUploading(false);
      if (fotoInputRef.current) fotoInputRef.current.value = '';
    }
  };

  const handleFotoUpload = async (e) => {
    const file = e.target.files?.[0];
    await handleFotoUploadFile(file);
  };

  const handleDokumentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('dokument', file);
      await uploadProjektDokument(projektId, formData);
      toast.success('Dokument hochgeladen');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler beim Hochladen');
    } finally {
      setUploading(false);
      if (dokumentInputRef.current) dokumentInputRef.current.value = '';
    }
  };

  const handleDeleteFoto = async (fotoId, e) => {
    e?.stopPropagation();
    if (!confirm('Foto wirklich löschen?')) return;
    try {
      await deleteProjektFoto(projektId, fotoId);
      toast.success('Foto gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleDeleteDokument = async (dokumentId) => {
    if (!confirm('Dokument wirklich löschen?')) return;
    try {
      await deleteProjektDokument(projektId, dokumentId);
      toast.success('Dokument gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleDownloadFoto = (foto, e) => {
    e?.stopPropagation();
    const link = document.createElement('a');
    link.href = foto.dateipfad;
    link.download = foto.dateiname;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadDokument = async (dokument) => {
    try {
      const response = await downloadProjektDokument(projektId, dokument.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', dokument.originalname);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Fehler beim Download');
    }
  };

  const handlePdfOpen = async (dokument) => {
    try {
      const response = await downloadProjektDokument(projektId, dokument.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Fehler beim Öffnen der PDF');
    }
  };

  // Dokument-Preview Handlers
  const handleDocMouseEnter = async (e, dok) => {
    if (isMobile) return;

    if (previewTimeout.current) {
      clearTimeout(previewTimeout.current);
    }

    const mouseX = e.clientX;
    const mouseY = e.clientY;

    previewTimeout.current = setTimeout(async () => {
      const popupWidth = 420;
      const isPdfDoc = dok.dateityp?.includes('pdf');
      const isImage = dok.dateityp?.includes('image');
      const popupHeight = isPdfDoc ? 520 : (isImage ? 350 : 200);

      let left = mouseX + 15;
      let top = mouseY + 15;

      if (left + popupWidth > window.innerWidth - 10) {
        left = mouseX - popupWidth - 15;
      }
      if (top + popupHeight > window.innerHeight - 10) {
        top = mouseY - popupHeight - 15;
      }

      setPreviewPosition({ top, left });
      setPreviewDokument(dok);

      // Für Bilder und PDFs: URL laden
      if (dok.dateityp?.includes('image') || dok.dateityp?.includes('pdf')) {
        try {
          const response = await downloadProjektDokument(projektId, dok.id);
          const blob = new Blob([response.data], { type: dok.dateityp });
          const url = window.URL.createObjectURL(blob);
          setPreviewUrl(url);
        } catch (error) {
          console.error('Preview Fehler:', error);
        }
      }
    }, 800);
  };

  const handleDocMouseLeave = () => {
    if (previewTimeout.current) {
      clearTimeout(previewTimeout.current);
    }
    previewTimeout.current = setTimeout(() => {
      setPreviewDokument(null);
      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
    }, 300);
  };

  const handlePreviewMouseEnter = () => {
    if (previewTimeout.current) {
      clearTimeout(previewTimeout.current);
    }
  };

  const handlePreviewMouseLeave = () => {
    setPreviewDokument(null);
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const closePreview = () => {
    if (previewTimeout.current) {
      clearTimeout(previewTimeout.current);
    }
    setPreviewDokument(null);
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  // Gefilterte Dokumente
  const filteredDokumente = dokumente.filter(dok => {
    if (filterKategorie && dok.kategorie !== filterKategorie) {
      return false;
    }
    return true;
  });

  const isPdf = (dateityp) => dateityp?.includes('pdf');

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Fotos Section */}
      <div className="bg-surface rounded-card shadow-card p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-text flex items-center gap-2">
            <Image className="w-5 h-5 text-text-muted" />
            Fotos ({fotos.length})
          </h3>
          {!readOnly && (
            <label className="flex items-center gap-2 px-3 py-2 bg-primary text-white rounded-button hover:bg-primary-dark transition text-sm cursor-pointer">
              <Plus className="w-4 h-4" />
              Foto hinzufügen
              <input
                ref={fotoInputRef}
                type="file"
                accept="image/*"
                onChange={handleFotoUpload}
                className="hidden"
                disabled={uploading}
              />
            </label>
          )}
        </div>

        {/* Drag & Drop Upload Zone */}
        {!readOnly && (
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center mb-4 transition ${
              dragActive
                ? 'border-primary bg-primary-light/20'
                : 'border-border hover:border-primary'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Upload className={`w-8 h-8 mx-auto mb-2 ${dragActive ? 'text-primary' : 'text-text-muted'}`} />
            <p className="text-sm text-text-muted">
              {uploading ? 'Wird hochgeladen...' : 'Fotos hier ablegen oder oben klicken'}
            </p>
          </div>
        )}

        {fotos.length === 0 ? (
          <div className="text-center py-8 text-text-muted">
            <Image className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Keine Fotos vorhanden</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {fotos.map((foto) => (
              <div
                key={foto.id}
                className="relative bg-surface border border-border rounded-lg overflow-hidden group cursor-pointer"
                onClick={() => setSelectedFoto(foto)}
              >
                {/* Image Preview */}
                <div className="aspect-square bg-gray-100">
                  <img
                    src={foto.dateipfad}
                    alt={foto.dateiname}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2">
                  <button
                    onClick={(e) => handleDownloadFoto(foto, e)}
                    className="p-2 bg-white rounded-lg hover:bg-gray-100 transition"
                    title="Download"
                  >
                    <Download className="w-5 h-5 text-text" />
                  </button>
                  {!readOnly && (
                    <button
                      onClick={(e) => handleDeleteFoto(foto.id, e)}
                      className="p-2 bg-red-500 rounded-lg hover:bg-red-600 transition"
                      title="Löschen"
                    >
                      <Trash2 className="w-5 h-5 text-white" />
                    </button>
                  )}
                </div>

                {/* Filename */}
                <div className="p-2 bg-white border-t border-border">
                  <p className="text-xs text-text-muted truncate" title={foto.dateiname}>
                    {foto.dateiname}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dokumente Section */}
      <div className="bg-surface rounded-card shadow-card p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-text flex items-center gap-2">
            <FileText className="w-5 h-5 text-text-muted" />
            Dokumente ({dokumente.length})
          </h3>
          {!readOnly && (
            <label className="flex items-center gap-2 px-3 py-2 bg-primary text-white rounded-button hover:bg-primary-dark transition text-sm cursor-pointer">
              <Plus className="w-4 h-4" />
              Dokument hinzufügen
              <input
                ref={dokumentInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png"
                onChange={handleDokumentUpload}
                className="hidden"
                disabled={uploading}
              />
            </label>
          )}
        </div>

        {/* Upload Box */}
        {!readOnly && (
          <label className="block border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary hover:bg-primary-light/10 transition mb-4">
            <input
              type="file"
              onChange={handleDokumentUpload}
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
              disabled={uploading}
            />
            <Upload className="w-8 h-8 mx-auto mb-2 text-text-muted" />
            {uploading ? (
              <p className="text-text-muted">Wird hochgeladen...</p>
            ) : (
              <>
                <p className="font-medium text-sm">Dokument hochladen</p>
                <p className="text-xs text-text-muted mt-1">
                  PDF, Word, Excel, Text oder Bilder (max. 20MB)
                </p>
              </>
            )}
          </label>
        )}

        {/* Filter */}
        {dokumente.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 p-3 bg-gray-50 rounded-lg mb-4">
            <div className="flex items-center gap-2 text-sm text-text-muted">
              <Filter className="w-4 h-4" />
              <span>Filter:</span>
            </div>

            <select
              value={filterKategorie}
              onChange={(e) => setFilterKategorie(e.target.value)}
              className="px-3 py-1.5 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white"
            >
              <option value="">Alle Kategorien</option>
              {KATEGORIEN.map((kat) => (
                <option key={kat} value={kat}>{kat}</option>
              ))}
            </select>

            {filterKategorie && (
              <button
                onClick={() => setFilterKategorie('')}
                className="px-3 py-1.5 text-sm text-primary hover:bg-primary-light rounded-lg transition"
              >
                Zurücksetzen
              </button>
            )}

            <span className="ml-auto text-sm text-text-muted">
              {filteredDokumente.length} von {dokumente.length} Dokumenten
            </span>
          </div>
        )}

        {dokumente.length === 0 ? (
          <div className="text-center py-8 text-text-muted">
            <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Noch keine Dokumente vorhanden</p>
          </div>
        ) : filteredDokumente.length === 0 ? (
          <div className="text-center py-8 text-text-muted">
            <Filter className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Keine Dokumente gefunden</p>
            <button
              onClick={() => setFilterKategorie('')}
              className="mt-2 text-primary hover:underline text-sm"
            >
              Filter zurücksetzen
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredDokumente.map((dok) => {
              const fileStyle = getFileIcon(dok.dateityp);
              const IconComponent = fileStyle.icon;
              const dokIsPdf = isPdf(dok.dateityp);

              return (
                <div
                  key={dok.id}
                  className="bg-white border border-border rounded-lg p-4 hover:shadow-md transition"
                  onMouseEnter={(e) => handleDocMouseEnter(e, dok)}
                  onMouseLeave={handleDocMouseLeave}
                >
                  <div className="flex items-start gap-3">
                    {/* Icon - klickbar für PDFs */}
                    {dokIsPdf ? (
                      <button
                        onClick={() => handlePdfOpen(dok)}
                        className={`p-3 rounded-lg ${fileStyle.bg} hover:opacity-80 transition cursor-pointer`}
                        title="PDF im Browser öffnen"
                      >
                        <IconComponent className={`w-6 h-6 ${fileStyle.color}`} />
                      </button>
                    ) : (
                      <div className={`p-3 rounded-lg ${fileStyle.bg}`}>
                        <IconComponent className={`w-6 h-6 ${fileStyle.color}`} />
                      </div>
                    )}

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm truncate" title={dok.originalname}>
                        {dok.originalname}
                      </h4>
                      <p className="text-xs text-text-muted mt-1">
                        {formatFileSize(dok.groesse)} • {new Date(dok.erstelltAm).toLocaleDateString('de-DE')}
                      </p>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-2 mt-2">
                        {dok.kategorie && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getKategorieBadge(dok.kategorie)}`}>
                            {dok.kategorie}
                          </span>
                        )}
                      </div>

                      {dok.beschreibung && (
                        <p className="text-xs text-text-muted mt-2 line-clamp-2">
                          {dok.beschreibung}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-1">
                      {dokIsPdf && (
                        <button
                          onClick={() => handlePdfOpen(dok)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition"
                          title="Öffnen"
                        >
                          <Eye className="w-4 h-4 text-text-muted" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDownloadDokument(dok)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition"
                        title="Herunterladen"
                      >
                        <Download className="w-4 h-4 text-text-muted" />
                      </button>
                      {!readOnly && (
                        <button
                          onClick={() => handleDeleteDokument(dok.id)}
                          className="p-2 hover:bg-red-50 rounded-lg transition"
                          title="Löschen"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Foto Lightbox */}
      {selectedFoto && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedFoto(null)}
        >
          <button
            onClick={() => setSelectedFoto(null)}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white/20 rounded-full transition"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={selectedFoto.dateipfad}
            alt={selectedFoto.dateiname}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          {/* Foto Info */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-lg">
            <p className="text-sm">{selectedFoto.dateiname}</p>
          </div>
        </div>
      )}

      {/* Dokument-Preview Popup */}
      {previewDokument && !isMobile && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="fixed z-50 bg-white shadow-2xl border border-gray-200 rounded-xl overflow-hidden"
          style={{
            top: previewPosition.top,
            left: previewPosition.left,
            width: '400px',
            maxWidth: 'calc(100vw - 16px)'
          }}
          onMouseEnter={handlePreviewMouseEnter}
          onMouseLeave={handlePreviewMouseLeave}
        >
          {/* Bild-Vorschau */}
          {previewDokument.dateityp?.includes('image') && previewUrl && (
            <div className="relative bg-gray-100">
              <img
                src={previewUrl}
                alt={previewDokument.originalname}
                className="w-full object-contain"
                style={{ maxHeight: '280px' }}
              />
            </div>
          )}

          {/* PDF-Vorschau */}
          {previewDokument.dateityp?.includes('pdf') && (
            <div className="relative bg-gray-100" style={{ height: '400px' }}>
              {previewUrl ? (
                <iframe
                  src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                  title="PDF Preview"
                  className="w-full h-full border-0"
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              )}
            </div>
          )}

          {/* Word-Vorschau */}
          {(previewDokument.dateityp?.includes('word') || previewDokument.dateityp?.includes('document')) && (
            <div className="relative bg-blue-50 h-32 flex items-center justify-center">
              <div className="text-center">
                <FileText className="w-12 h-12 text-blue-500 mx-auto mb-2" />
                <p className="text-sm text-blue-600 font-medium">Word-Dokument</p>
              </div>
            </div>
          )}

          {/* Excel-Vorschau */}
          {(previewDokument.dateityp?.includes('excel') || previewDokument.dateityp?.includes('spreadsheet')) && (
            <div className="relative bg-green-50 h-32 flex items-center justify-center">
              <div className="text-center">
                <FileSpreadsheet className="w-12 h-12 text-green-500 mx-auto mb-2" />
                <p className="text-sm text-green-600 font-medium">Excel-Dokument</p>
              </div>
            </div>
          )}

          {/* Info-Bereich */}
          <div className="p-3 bg-white border-t border-gray-100">
            <p className="text-sm font-medium text-gray-900 truncate">{previewDokument.originalname}</p>
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-gray-500">{formatFileSize(previewDokument.groesse)}</span>
              <div className="flex gap-1">
                {isPdf(previewDokument.dateityp) && (
                  <button
                    onClick={() => {
                      handlePdfOpen(previewDokument);
                      closePreview();
                    }}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs bg-primary text-white rounded-lg hover:bg-primary-dark transition"
                  >
                    <Eye className="w-3 h-3" />
                    Öffnen
                  </button>
                )}
                <button
                  onClick={() => {
                    handleDownloadDokument(previewDokument);
                    closePreview();
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                >
                  <Download className="w-3 h-3" />
                  Download
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Upload Indicator */}
      {uploading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface rounded-card p-6 flex items-center gap-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            <span className="text-text">Wird hochgeladen...</span>
          </div>
        </div>
      )}
    </div>
  );
}
